// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck
import * as React from 'react';

import { cn } from '@/lib/utils';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          'flex h-9 w-full rounded-md group-has-[.field-error-message]:border-destructive border bg-transparent px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

type FormInputRegisterOptions = RegisterOptions<any, string>;

const get = (object: object, path: string) => {
  const isNested = path.includes('.');
  if (isNested) {
    const segments = path.split('.');
    const firstSegment = segments[0];
    const newPath = segments.slice(1).join('.');
    if (!object[firstSegment]) {
      return null;
    }

    return get(object[firstSegment], newPath);
  }

  return object[path];
};

const CommonFormInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & {
    classNameContainer?: string;
    registerOptions?: FormInputRegisterOptions;
  }
>(({ registerOptions, ...props }, ref) => {
  const { register } = useFormContext();
  if (!props.name) {
    throw new Error('No field name provided');
  }
  const registerReturn = register(props.name, registerOptions);

  return <Input ref={ref} {...props} {...registerReturn} />;
});

const FormInputErrorMessage = ({
  name,
  className,
  ...props
}: React.ComponentProps<'div'> & { name: string }) => {
  const { formState } = useFormContext();
  const errorMessage = get(formState.errors, name)?.message;

  if (!errorMessage) {
    return null;
  }

  return (
    <div
      className={cn(
        'text-xs field-error-message text-destructive mt-1',
        className
      )}
      {...props}
    >
      {errorMessage}
    </div>
  );
};

const SingleFileInput = (
  props: React.ComponentProps<'input'> & { labelvalue?: string; testid: string; 'data-testid'?: string }
) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [labelvalue, setLabelValue] = React.useState(props?.labelvalue ?? '');

  const { formState } = useFormContext();

  const errorMessage =
    props?.name && get(formState.errors, props.name)?.message;
  const isInvalid = Boolean(errorMessage) || !props.name;

  const { t } = useTranslation();

  return (
    <div>
      <Input
        {...props}
        name={props?.name}
        type='file'
        multiple={false}
        ref={inputRef}
        className={cn('hidden', props?.className)}
        onChange={async (e) => {
          await props?.onChange?.(e);
          setTimeout(() => {
            const firstFileName = inputRef?.current?.files?.[0]?.name;
            if (firstFileName) {
              setLabelValue(firstFileName);
            }
            //macro task to upadte label display
          }, 0);
        }}
      />
      <div
        className={cn(
          'py-2 px-3 border-border border-[1px] rounded-[6px] flex items-center',
          `${isInvalid && 'border-destructive'}`
        )}
      >
        {!labelvalue && (
          <div className='text-sm mr-2 font-medium'>
            {t('form.choose_file')}
          </div>
        )}
        <label
          htmlFor={props?.name}
          data-testid={props.testid}
          className='text-sm overflow-hidden text-ellipsis cursor-pointer'
        >
          {labelvalue ? labelvalue : t('form.file_not_selected')}
        </label>
      </div>
      {props?.name && <FormInputErrorMessage name={props.name} />}
    </div>
  );
};

CommonFormInput.displayName = 'CommonFormInput';
Input.displayName = 'Input';

export { Input, CommonFormInput, FormInputErrorMessage, SingleFileInput };
